import { Box, Drawer, Toolbar, Typography, IconButton, Grid, Button, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from '@tanstack/react-form';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';
import { useBillingItemsQuery } from '../../../stores/FetcherStores/BillingItemFetcher';
import useCompanyId from '../../../hooks/useCompanyId';
import { SubscriptionBillingItem } from '../../../model/BillingItem';
import { useUpdateSubscriptionMutation } from '../../../stores/FetcherStores/SubscriptionFetcher';

interface EditSubscriptionDrawerProps {
  open: boolean;
  onClose: () => void;
  billingItemId: string | null;
}

const EditSubscriptionDrawer = observer(
  ({ open, onClose, billingItemId }: EditSubscriptionDrawerProps) => {
    const { t } = useTranslation();
    const companyId = useCompanyId();
    const { data: billingItems } = useBillingItemsQuery(companyId);
    const billingItem = billingItems?.find(
      (item) => item.id === billingItemId,
    ) as SubscriptionBillingItem;
    const {
      mutateAsync: updateSubscription,
      isLoading: isUpdatingSubscription,
      error,
    } = useUpdateSubscriptionMutation();

    useEffect(() => {
      form.reset();
    }, [billingItem]);

    const form = useForm({
      defaultValues: {
        endDate: billingItem?.endDate ? DateTime.fromISO(billingItem.endDate) : null,
      },
      onSubmit: async ({ value }) => {
        await updateSubscription(companyId, billingItem.subscriptionId, {
          endDate: value.endDate?.toISO() || undefined,
        })
          .then(() => {
            onClose();
          })
          .catch(() => {});
      },
    });
    const errorMessage =
      error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message;

    if (!billingItem) {
      return null;
    }

    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box
          sx={{
            maxWidth: '100%',
            width: 600,
            height: '100%',
            paddingRight: 4,
            paddingLeft: 4,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              marginTop: 12,
              marginBottom: 2,
              display: 'flex',
              minHeight: 'initial !important',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography variant="h4">Ändra prenumeration</Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Toolbar>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Typography variant="h6">Prenumeration</Typography>
            <Typography variant="body1">
              {t(`subscriptions.${billingItem.subscriptionId}`)}
            </Typography>
            <Typography variant="h6">Startdatum</Typography>
            <Typography variant="body1">
              {DateTime.fromISO(billingItem.startDate).toFormat('yyyy-MM-dd')}
            </Typography>
            <Typography variant="h6">Slutdatum</Typography>
            {/* show information about the subscription. Add setting to end it now, or at a specific date */}
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
              sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              <form.Field name="endDate">
                {(field) => (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <DatePicker
                        sx={{ width: '100%' }}
                        onChange={(date) => field.handleChange(date)}
                        value={field.state.value}
                        openTo="month"
                        views={['year', 'month', 'day']}
                        format="yyyy-MM-dd"
                        slotProps={{
                          textField: {
                            label: 'Slutdatum (valfritt)',
                            helperText: field.state.meta.errors.join(', '),
                            error: field.state.meta.errors.length > 0,
                          },
                        }}
                      />
                      <Button
                        onClick={() => field.handleChange(DateTime.now())}
                        variant="contained"
                        color="error"
                      >
                        Avsluta
                      </Button>
                    </Box>
                  </Grid>
                )}
              </form.Field>
              <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 4 }}>
                <Button variant="outlined" onClick={onClose} color="secondary">
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isUpdatingSubscription}
                  sx={{
                    backgroundColor: '#1db954',
                    '&:hover': {
                      backgroundColor: '#1a9f4a',
                    },
                  }}
                >
                  {isUpdatingSubscription ? 'Sparar...' : 'Spara'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    );
  },
);

export default EditSubscriptionDrawer;
